import React, { useRef, useState, useEffect } from 'react';
import { navigate } from './Router';
import { useTheme } from '../contexts/ThemeContext';


interface ErrorPageProps {
  type: '404' | '403';
  title?: string;
}

// インタラクティブ背景キューブコンポーネント (Login.tsxから同じもの)
const InteractiveCubeBackground = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cubes, setCubes] = useState<React.ReactElement[]>([]);
  const { theme } = useTheme();

  useEffect(() => {
    const createCubes = () => {
      const cubeElements: React.ReactElement[] = [];
      const cubeSize = 30;
      
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      const cols = Math.ceil(screenWidth / cubeSize) + 2;
      const rows = Math.ceil(screenHeight / cubeSize) + 2;
      
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const key = `cube-${row}-${col}`;
          cubeElements.push(
            <div
              key={key}
              className={`absolute border ${
                theme === 'dark' ? 'border-zinc-700/30' : 'border-gray-200/50'
              }`}
              style={{
                width: `${cubeSize}px`,
                height: `${cubeSize}px`,
                left: `${col * cubeSize - cubeSize}px`,
                top: `${row * cubeSize - cubeSize}px`,
                backgroundColor: theme === 'dark' ? 'rgb(24, 24, 27)' : 'rgb(249, 250, 251)',
                transition: 'all 2s ease-out',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transition = 'none';
                e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgb(107, 114, 128)' : 'rgb(229, 231, 235)';
                e.currentTarget.style.borderColor = theme === 'dark' ? 'rgb(107, 114, 128)' : 'rgb(209, 213, 219)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transition = 'all 2s ease-out';
                e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgb(24, 24, 27)' : 'rgb(249, 250, 251)';
                e.currentTarget.style.borderColor = theme === 'dark' ? 'rgba(63, 63, 70, 0.3)' : 'rgba(229, 231, 235, 0.5)';
              }}
            />
          );
        }
      }
      
      setCubes(cubeElements);
    };

    createCubes();
    
    const handleResize = () => createCubes();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [theme]);

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 overflow-hidden pointer-events-none"
      style={{ zIndex: 1 }}
    >
      <div className="relative w-full h-full">
        {cubes.map((cube, index) => (
          <div key={index} className="pointer-events-auto">
            {cube}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ErrorPage({
  type,
  title
}: ErrorPageProps) {
  const { theme } = useTheme();

  const defaultTitles = {
    '404': 'Page Not Found',
    '403': 'Access Forbidden'
  };

  const errorTitle = title || defaultTitles[type];

  return (
    <div className="min-h-screen bg-theme-primary flex items-center justify-center p-4 relative">
      {/* インタラクティブキューブ背景 */}
      <InteractiveCubeBackground />
      
      {/* Back to Home link */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 text-brand-primary hover:text-brand-hover transition-colors text-sm z-50"
      >
        ← Back to Home
      </button>
      
      <div className="w-full max-w-md relative z-10">
        <div className={`${
          theme === 'dark' 
            ? 'bg-zinc-800/50 border-zinc-700/50' 
            : 'bg-white/80 border-gray-200'
        } backdrop-blur-lg rounded-2xl shadow-2xl border p-8`}>
          {/* Logo/Title */}
          <div className="text-center mb-8">
            <button 
              onClick={() => navigate('/')}
              className="group transition-all duration-200 hover:scale-105 active:scale-95"
            >
              <h1 className="text-3xl sm:text-4xl fira-sans-extra-bold-italic text-brand-primary mb-2 group-hover:text-brand-hover transition-colors">
                pixel moment
              </h1>
            </button>
            <p className="text-theme-tertiary text-sm">
              Transform your moments into pixel art
            </p>
          </div>

          {/* Error Content */}
          <div className="text-center">            
            {/* Error Code */}
            <div className={`text-6xl font-bold mb-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {type}
            </div>
            
            {/* Error Title */}
            <h2 className="text-xl font-semibold text-theme-primary mb-6">
              {errorTitle}
            </h2>

            {/* Action Button */}
            <button
              onClick={() => navigate('/')}
              className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                theme === 'dark'
                  ? 'bg-white hover:bg-gray-100 text-gray-900'
                  : 'bg-gray-900 hover:bg-gray-800 text-white'
              } hover:scale-105 active:scale-95 shadow-lg`}
            >
              Back to Home
            </button>

          </div>
        </div>

        {/* Bottom text */}
        <div className="text-center mt-6 text-theme-tertiary text-xs">
          © 2025 pixel moment. Every moment into pixel art.
        </div>
      </div>
    </div>
  );
}